import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { US_STATES, CA_PROVINCES, COUNTRIES } from "@/utils/locationData";
import { ChevronsUpDown, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicAxios = axios.create({ withCredentials: false });

export default function ResellerRequestModal({ open, onOpenChange }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [formData, setFormData] = useState({
    business_name: "",
    contact_email: "",
    contact_name: "",
    phone: "",
    business_address: {
      address: "",
      city: "",
      state: "",
      zipcode: "",
      country: "US"
    },
    business_website: "",
    preferred_subdomain: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Its own endpoint, not the shared /onboarding/request.
      //
      // That route is now the Infinity Touch signup and requires a distributor
      // referral code. eDisplayroom is a different product with its own sales
      // motion and no referral scheme, so it gets its own route — which also
      // means the exemption cannot be reached from the Infinity Touch endpoint
      // by setting a field. module_type and source are pinned server-side there,
      // so they no longer need to be sent (and can no longer be spoofed).
      await publicAxios.post(`${API}/onboarding/edisplayroom-request`, formData);
      toast.success("Request submitted successfully! We'll contact you soon.");
      onOpenChange(false);
      setFormData({
        business_name: "",
        contact_email: "",
        contact_name: "",
        phone: "",
        business_address: {
          address: "",
          city: "",
          state: "",
          zipcode: "",
          country: "US"
        },
        business_website: "",
        preferred_subdomain: "",
        message: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.detail || "Failed to submit request");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Request Your Store
          </DialogTitle>
          <DialogDescription>
            Fill in your details and we will set up your personalized store.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="business_name">Business Name *</Label>
              <Input
                id="business_name"
                required
                value={formData.business_name}
                onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                placeholder="Your Business Name"
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="business_website">Business Website *</Label>
              <Input
                id="business_website"
                required
                value={formData.business_website}
                onChange={(e) => setFormData(prev => ({ ...prev, business_website: e.target.value }))}
                placeholder="www.yourbusiness.com"
                className="h-12"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_name">Your Name *</Label>
              <Input
                id="contact_name"
                required
                value={formData.contact_name}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                placeholder="John Doe"
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_email">Email *</Label>
              <Input
                id="contact_email"
                type="email"
                required
                value={formData.contact_email}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                placeholder="john@example.com"
                className="h-12"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone (Optional)</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="+1 234 567 8900"
              className="h-12"
            />
          </div>

          <div className="space-y-4 pt-2 border-t border-slate-100">
            <div className="space-y-2">
              <Label htmlFor="address">Business Address *</Label>
              <Input
                id="address"
                required
                value={formData.business_address.address}
                onChange={(e) => setFormData(prev => ({ ...prev, business_address: { ...prev.business_address, address: e.target.value } }))}
                placeholder="Street address"
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">Town / City *</Label>
              <Input
                id="city"
                required
                value={formData.business_address.city}
                onChange={(e) => setFormData(prev => ({ ...prev, business_address: { ...prev.business_address, city: e.target.value } }))}
                placeholder="City"
                className="h-12"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Country *</Label>
                <Select
                  value={formData.business_address.country}
                  onValueChange={(val) => {
                    setFormData(prev => ({
                      ...prev,
                      business_address: { ...prev.business_address, country: val, state: "" }
                    }));
                  }}
                >
                  <SelectTrigger className="h-12 bg-white border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>State *</Label>
                <Popover open={stateDropdownOpen} onOpenChange={setStateDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={stateDropdownOpen}
                      className={cn(
                        "w-full justify-between h-12 border-slate-200 rounded-md font-normal",
                        !formData.business_address.state && "text-muted-foreground"
                      )}
                    >
                      {formData.business_address.state
                        ? [...US_STATES, ...CA_PROVINCES].find((s) => s.value === formData.business_address.state)?.label
                        : formData.business_address.country === 'CA' ? "Choose a Province" : "Choose a State"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="center">
                    <Command>
                      <CommandInput placeholder={formData.business_address.country === 'CA' ? "Search province..." : "Search state..."} className="h-9" />
                      <CommandEmpty>{formData.business_address.country === 'CA' ? "No province found." : "No state found."}</CommandEmpty>
                      <CommandList>
                        <CommandGroup>
                          {(formData.business_address.country === 'CA' ? CA_PROVINCES : US_STATES).map((state) => (
                            <CommandItem
                              key={state.value}
                              value={state.label}
                              onSelect={() => {
                                setFormData(prev => ({ ...prev, business_address: { ...prev.business_address, state: state.value } }));
                                setStateDropdownOpen(false);
                              }}
                            >
                              {state.label}
                              <Check
                                className={cn(
                                  "ml-auto h-4 w-4",
                                  formData.business_address.state === state.value ? "opacity-100" : "opacity-0"
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="zipcode">Zipcode *</Label>
                <Input
                  id="zipcode"
                  required
                  value={formData.business_address.zipcode}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_address: { ...prev.business_address, zipcode: e.target.value } }))}
                  placeholder="Zipcode"
                  className="h-12"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Additional Message (Optional)</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Tell us about your funeral home or business and any specific requirements..."
              rows={3}
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-slate-800 hover:bg-slate-900 rounded-full h-12 text-lg font-bold"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Application"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
